import {
    Badge,
    Button,
    Dialog,
    DialogContent,
    Tooltip,
} from "@jhool-io/fe-components";
import { isToday } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { onValue, query, ref, off } from "firebase/database";
import { APP_COLORS } from "../../../../../../utils/constants";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../../../utils/helpers";
import RefreshClockIcon from "../../../../../../components/Icons/RefreshClock";
import MultiplyCircleIcon from "../../../../../../components/Icons/MultiplyCircle";
import CancelSessionForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/CancelSessionForm/CancelSessionForm";
import EditSessionTimeForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/EditSessionTimeForm/EditSessionTimeForm";
import {
    ITelehealthCallParticipants,
    RecurrenceRule,
} from "../../../../../../utils/types";
import RecordIcon from "../../../../../../components/Icons/Record";
import useNewBackground from "../../../../../../hooks/useNewBackground";
import { IProviderUpcomingSession } from "../../../../../providers/types/providers.types";
import { rtdb } from "../../../../../../firebase";

interface UpcomingAppointmentProps {
    appointment: IProviderUpcomingSession;
}

type Dialogs = "cancel_session" | "reschedule_session";

export default function UpcomingAppointment({
    appointment,
}: UpcomingAppointmentProps) {
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [participants, setParticipants] = useState<
        ITelehealthCallParticipants[]
    >([]);

    const isCancelSessionMutating = useIsMutating(["cancel-session"]);
    const isEditTimeMutating = useIsMutating(["edit-session-time"]);

    const useNewBg = useNewBackground();

    const isClientOnCall = useMemo(
        () =>
            participants
                .filter((participant) => participant.status === "active")
                .some((participant) => participant.role === "client"),
        [participants]
    );

    const isDateToday = appointment.start_date_time
        ? isToday(new Date(appointment.end_date_time))
        : false;

    const clientOnCallDetails = useMemo(() => {
        return participants?.find(
            (participant) => participant.role === "client"
        );
    }, [participants]);

    const clientLeftCallText = useMemo(
        () =>
            isDateToday &&
            clientOnCallDetails &&
            clientOnCallDetails.status === "inactive" &&
            isToday(new Date(clientOnCallDetails?.updated_at)) ? (
                <div className="text-black text-xs italic flex items-center gap-x-[2px] font-medium">
                    <span className="size-8 rounded-full bg-danger blink" />
                    Client left call at
                    {` ${formatDate(
                        clientOnCallDetails.updated_at,
                        false,
                        "hh:mmaa"
                    )}`}
                </div>
            ) : null,
        [clientOnCallDetails, isDateToday]
    );

    const appointmentDateToDisplay = isDateToday
        ? "Today"
        : formatDate(
              appointment.start_date_time || "",
              false,
              "hh:mmaa. eee. MM/dd"
          );

    const getAppointmentLocationInfoToShow = () => {
        if (appointment.is_paid && appointment.session_history_id) {
            return (
                <Link
                    to={`/meeting/${
                        appointment.session_history_id
                    }/join?with=${`${appointment.client.first_name}_${appointment.client.last_name}`}`}
                    className="rounded-r8 bg-primary px-8 py-[2px] flex items-center gap-x-[2px] text-xs font-medium border border-strokedark
                    shadow-[0px_1px_1px_0px_rgba(0,0,0,0.10),0px_1px_1px_0pxrgba(0,0,0,0.10)] text-white"
                >
                    <RecordIcon stroke="#ffffff" /> Join now
                </Link>
            );
        }
        if (appointment.delivery_option === "remote") {
            return (
                <Badge
                    content="Telemed"
                    textColor={APP_COLORS.GRAY_900}
                    bgColor={APP_COLORS.GRAY_300}
                    className="cursor-text"
                />
            );
        }
        return (
            <Badge
                content="In person"
                textColor={APP_COLORS.GRAY_900}
                bgColor={APP_COLORS.GRAY_300}
                className="cursor-text"
            />
        );
    };

    const getCardBgColor = () => {
        switch (appointment.recurrence_rule) {
            case RecurrenceRule.MONTHLY:
                return "#FFEDF6";

            case RecurrenceRule.WEEKLY:
                return "#EDF9FF";

            case RecurrenceRule.BIWEEKLY:
                return "#E9F9F0";

            case RecurrenceRule.ONE_OFF:
                return "#FFEBED";

            case RecurrenceRule.TRIWEEKLY:
                return "#EDF9FF";

            case RecurrenceRule.TWO_MONTHS:
                return "#FEF5E9";

            case RecurrenceRule.THREE_MONTHS:
                return "#F1F1F3";

            default:
                return "#F1F1F3";
        }
    };

    useEffect(() => {
        const callRef = query(
            ref(rtdb, `/telehealth_calls/${appointment.session_history_id}`)
        );

        const unsubscribe = onValue(callRef, (snapShot) => {
            const callParticipants = snapShot.val();

            if (callParticipants) {
                const keys = Object.keys(callParticipants);
                let partcipantsArray: ITelehealthCallParticipants[] = [];

                keys.forEach((p) => {
                    partcipantsArray = [
                        {
                            ...callParticipants[p],
                        },
                        ...partcipantsArray,
                    ];
                });

                setParticipants(partcipantsArray);
            }
        });

        return () => {
            off(callRef, "value", unsubscribe);
        };
    }, [appointment.session_history_id]);

    return (
        <>
            <Dialog open={dialogInView === "cancel_session"}>
                <DialogContent
                    title="Cancel session"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => setDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    classNames="pt-24 pb-48"
                    onCancelClick={() => setShouldNavigate(false)}
                    cancelText="Yes,cancel"
                    saveText="No,create No show note"
                    canCancelButtonSubmit
                    cancelBtnFormValue="cancel_client_session"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => setDialogInView(null)}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={
                            appointment.session_history_id || null
                        }
                        action="cancel"
                    />
                </DialogContent>
            </Dialog>
            {appointment.session_history_id &&
            appointment.start_date_time &&
            appointment.end_date_time ? (
                <Dialog open={dialogInView === "reschedule_session"}>
                    <DialogContent
                        title="Edit Session Time"
                        variant="center"
                        handleCloseDialog={() => setDialogInView(null)}
                        isCancelBtnDisabled={isEditTimeMutating > 0}
                        isSubmitBtnDisabled={isEditTimeMutating > 0}
                        showFooter
                        submitBtnFormValue="edit-session-time"
                        classNames="pb-40"
                    >
                        <EditSessionTimeForm
                            sessionHistoryId={appointment.session_history_id}
                            sessionStartDateTime={appointment.start_date_time}
                            sessionEndDateTime={appointment.end_date_time}
                            onFormSubmit={() => {
                                setDialogInView(null);
                            }}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <div
                className={`py-8 px-16 rounded-r12 relative group flex flex-col gap-y-12 ${
                    useNewBg ? "bg-secondary100" : ""
                }`}
                style={{
                    backgroundColor: useNewBg ? undefined : getCardBgColor(),
                }}
            >
                <div className="flex flex-col min-[500px]:flex-row items-start min-[500px]:items-center justify-between gap-x-4 gap-y-16 flex-wrap">
                    <span className="text-sm font-medium text-strong950 tracking-[-0.084px] leading-8">
                        {displayNameInRightFormat({
                            firstName: appointment.client.first_name,
                            lastName: appointment.client.last_name,
                        })}
                    </span>
                    <div className="flex items-center gap-x-12 gap-y-12 flex-wrap">
                        <span className="text-[#525866] font-normal text-xs leading-[16px]">
                            {appointmentDateToDisplay}
                        </span>
                        {appointment.is_paid ? (
                            <Badge
                                content="Paid"
                                textColor={APP_COLORS.GREEN_900}
                                bgColor={APP_COLORS.GREEN_300}
                            />
                        ) : (
                            <Badge
                                content="Unpaid"
                                textColor="#711920"
                                bgColor="#FEC1C6"
                            />
                        )}
                        {getAppointmentLocationInfoToShow()}
                    </div>
                </div>
                <div className="flex items-center justify-between gap-x-8">
                    <div className="px-[6px] h-[28px] inline-flex items-center gap-x-[18px] rounded-r6 bg-white border border-[rgba(231,231,231,0.91)] shadow-[0px_6px_10px_0px_rgba(14,18,27,0.06),_0px_2px_4px_0px_rgba(14,18,27,0.03)] self-start">
                        {appointment.session_history_id &&
                        appointment.start_date_time &&
                        appointment.end_date_time ? (
                            <Tooltip content="Reschedule">
                                <Button
                                    size="auto"
                                    variant="normal"
                                    className="p-4 hover:bg-secondary-light"
                                    onClick={() =>
                                        setDialogInView("reschedule_session")
                                    }
                                >
                                    <RefreshClockIcon />
                                </Button>
                            </Tooltip>
                        ) : null}
                        <Tooltip content="Cancel session">
                            <Button
                                size="auto"
                                variant="normal"
                                className="p-4 hover:bg-secondary-light"
                                onClick={() =>
                                    setDialogInView("cancel_session")
                                }
                            >
                                <MultiplyCircleIcon />
                            </Button>
                        </Tooltip>
                    </div>
                    {isClientOnCall ? (
                        <div className="text-black text-xs italic flex items-center gap-x-[2px] font-medium">
                            <span className="size-8 rounded-full bg-primary blink" />
                            Client has joined the call
                        </div>
                    ) : (
                        clientLeftCallText
                    )}
                </div>
            </div>
        </>
    );
}
